import { formatDateTime, formatMoney, t } from "@hakili/i18n";
import { ArrowLeft, Clock } from "lucide-react";

/**
 * `AVAILABILITY-QUEUE`, `M2`, point 3: **« Véhicules disponibles pour vous »**,
 * `vehicule.tsx`'s own sibling and the same shape , a props-driven,
 * presentational screen with no door of its own, the data-fetching wired in
 * `App.tsx` exactly where `vehicleDetail`'s is.
 *
 * **One row per FRONT position, and the server already dropped a lapsed
 * one.** `GET /api/v1/me/queue-front` answers only entries whose
 * `front_expires_at` is still ahead of the server's own clock
 * (`CLIENT_FRONT_ENTRIES_SQL`), so this screen never has to decide for
 * itself whether a row is stale , it renders what it was handed, and a
 * lapsed entry simply is not in the list by the time the TTL sweep or the
 * next read catches it.
 *
 * **The expiry is shown as the ABSOLUTE instant, never a countdown computed
 * against the ambient clock.** `offres.tsx`'s own `valableJusquAu` is the
 * precedent: `9.2.4`'s `D2` bans `Date.now()` outside `platform/src/clock`
 * everywhere in this tree, apps included, and this screen has no `Clock` to
 * inject. `formatDateTime` needs none , it converts an instant this screen
 * already has, which is exactly the argument `new Date(value)` form `D2`
 * admits.
 *
 * **The action is the existing door, not a new one.** `WF-CLI-08`/`WF-CLI-09`'s
 * `engage()` is reached through the car's own fiche (`vehicule.tsx`), so a
 * card's control here names the car and hands back its id; `App.tsx` routes
 * that id to `/vehicule/:id` exactly as the search screen already does.
 */

export interface FrontQueueEntry {
  readonly queueEntryId: string;
  readonly vehicleId: string;
  readonly vehicleTitle: string;
  readonly priceAmount: number;
  readonly frontAt: string;
  readonly frontExpiresAt: string;
}

export interface QueueFrontScreenProps {
  readonly entries: readonly FrontQueueEntry[];
  readonly onBack: () => void;
  readonly onChoose: (vehicleId: string) => void;
}

export function QueueFrontScreen({ entries, onBack, onChoose }: QueueFrontScreenProps): React.JSX.Element {
  return (
    <div className="flex flex-col px-5 pb-4 pt-4">
      <div className="mb-4 flex items-center gap-3">
        <button
          type="button"
          data-testid="queue-front-retour"
          aria-label={t("common.action.retour")}
          onClick={onBack}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white"
        >
          <ArrowLeft className="h-4 w-4 text-gray-600" aria-hidden="true" />
        </button>
        <h1 className="text-lg font-semibold tracking-tight text-gray-900">{t("client.queueFront.titre")}</h1>
      </div>
      <p className="mb-4 text-sm leading-relaxed text-gray-500">{t("client.queueFront.resume")}</p>

      {entries.length === 0 ? (
        <p className="rounded-2xl border border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-500">
          {t("client.queueFront.vide")}
        </p>
      ) : (
        <ul className="space-y-3">
          {entries.map((entry) => (
            <li
              key={entry.queueEntryId}
              data-testid={`queue-front-entree-${entry.vehicleId}`}
              className="overflow-hidden rounded-2xl border border-gray-200 bg-white"
            >
              <div className="px-4 py-3.5">
                <p className="text-sm font-semibold text-gray-900">{entry.vehicleTitle}</p>
                <p className="mt-0.5 text-sm tabular-nums text-gray-500">{formatMoney(entry.priceAmount)}</p>
                <p
                  data-testid={`queue-front-expiry-${entry.vehicleId}`}
                  className="mt-2 flex items-center gap-1.5 text-xs font-medium text-amber-600"
                >
                  <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {t("client.queueFront.expireLe")} {formatDateTime(new Date(entry.frontExpiresAt))}
                </p>
              </div>
              <button
                type="button"
                data-testid={`queue-front-continuer-${entry.vehicleId}`}
                onClick={() => {
                  onChoose(entry.vehicleId);
                }}
                className="w-full border-t border-gray-100 bg-gray-50 py-3 text-sm font-medium text-indigo-600 hover:bg-gray-100"
              >
                {t("client.queueFront.continuer")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
